/**
 * Search Overlay Functionality - Team Line Custom Theme
 * Centered search UI like Backdoor
 * 
 * Features:
 * - Click search icon to open centered overlay
 * - Type to search with live product results
 * - 6 products on desktop grid, carousel on mobile
 * - ESC key or click outside to close
 * - Reuses existing product card styling
 */

(function() {
  'use strict';

  // Elements
  let searchOverlay = null;
  let searchInput = null;
  let searchForm = null;
  let clearButton = null;
  let suggestionsSection = null;
  let resultsSection = null;
  let productsContainer = null;
  let loadingSection = null;
  let noResultsSection = null;
  let resultsTitle = null;
  let seeAllLink = null;
  let seeAllEmptyLink = null;
  let searchQuerySpan = null;
  
  let isOpen = false;
  let searchTimeout = null;
  let isDraggingCarousel = false; // Prevent closing during carousel drag
  const body = document.body;

  // Open search overlay
  const openSearch = () => {
    if (isOpen) return;
    
    searchOverlay = searchOverlay || document.querySelector('[data-search-overlay]');
    searchInput = searchInput || document.querySelector('.search-overlay__input');
    const header = document.querySelector('[data-header]');
    const searchHeader = document.querySelector('.search-overlay__header');
    const searchContent = document.querySelector('.search-overlay__content');
    
    if (!searchOverlay) {
      console.warn('[Search] Overlay not found');
      return;
    }
    
    isOpen = true;
    searchOverlay.classList.add('is-active');
    body.classList.add('search-open');
    body.style.overflow = 'hidden';
    
    // Position the search bar to overlay the header main row
    if (header && searchHeader) {
      const headerMain = header.querySelector('.header__main');
      const promoBanner = header.querySelector('.header__promo-banner');
      
      if (headerMain) {
        const mainRect = headerMain.getBoundingClientRect();
        const promoHeight = promoBanner ? promoBanner.offsetHeight : 0;
        
        // Set search header to match header main position + extra offset
        const extraOffset = 8; // Move search form down a bit
        searchHeader.style.paddingTop = (promoHeight + extraOffset) + 'px';
        searchHeader.style.minHeight = mainRect.height + 'px';
      }
    }
    
    // Position the content to start below the full header
    if (header && searchContent) {
      const headerRect = header.getBoundingClientRect();
      searchContent.style.marginTop = '0';
    }
    
    // Focus input after animation
    if (searchInput) {
      setTimeout(() => searchInput.focus(), 100);
    }
    
    console.log('[Search] Overlay opened');
  };

  // Close search overlay
  const closeSearch = () => {
    if (!isOpen) return;
    
    searchOverlay = searchOverlay || document.querySelector('[data-search-overlay]');
    if (!searchOverlay) return;
    
    isOpen = false;
    searchOverlay.classList.remove('is-active');
    body.classList.remove('search-open');
    body.style.overflow = '';
    
    // Reset state
    if (searchInput) {
      searchInput.value = '';
    }
    if (clearButton) {
      clearButton.classList.remove('is-visible');
    }
    
    // Show suggestions, hide results
    showSuggestions();
    
    console.log('[Search] Overlay closed');
  };

  // Show suggestions section (initial state)
  const showSuggestions = () => {
    if (suggestionsSection) suggestionsSection.style.display = 'block';
    if (resultsSection) resultsSection.style.display = 'none';
    if (loadingSection) loadingSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'none';
  };

  // Show loading state
  const showLoading = () => {
    if (suggestionsSection) suggestionsSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'none';
    if (loadingSection) loadingSection.style.display = 'flex';
    if (noResultsSection) noResultsSection.style.display = 'none';
  };

  // Show results
  const showResults = () => {
    if (suggestionsSection) suggestionsSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'block';
    if (loadingSection) loadingSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'none';
  };

  // Show no results
  const showNoResults = (query) => {
    if (suggestionsSection) suggestionsSection.style.display = 'none';
    if (resultsSection) resultsSection.style.display = 'none';
    if (loadingSection) loadingSection.style.display = 'none';
    if (noResultsSection) noResultsSection.style.display = 'block';
    if (searchQuerySpan) searchQuerySpan.textContent = query;
  };

  // Perform search
  const performSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      showSuggestions();
      return;
    }

    showLoading();

    try {
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12`;
      
      const response = await fetch(searchUrl, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`Search API error: ${response.status}`);
      }
      
      const data = await response.json();

      // Extract products from response
      let products = [];
      if (data.resources && data.resources.results && data.resources.results.products) {
        products = data.resources.results.products;
      } else if (data.products && Array.isArray(data.products)) {
        products = data.products;
      }

      if (products.length === 0) {
        showNoResults(query);
        updateSeeAllLinks(query);
        return;
      }

      // Update results title
      if (resultsTitle) {
        resultsTitle.textContent = `Results for "${query}"`;
      }
      
      // Update see all link
      updateSeeAllLinks(query);

      // Render product cards
      renderProducts(products);
      showResults();

    } catch (error) {
      console.error('[Search] Error:', error);
      showNoResults(query);
      updateSeeAllLinks(query);
    }
  };

  // Update see all links
  const updateSeeAllLinks = (query) => {
    const searchPageUrl = `${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}`;
    if (seeAllLink) {
      seeAllLink.href = searchPageUrl;
      seeAllLink.textContent = `See All`;
    }
    if (seeAllEmptyLink) {
      seeAllEmptyLink.href = searchPageUrl;
    }
  };

  // Render products using the same card structure as the theme
  const renderProducts = (products) => {
    if (!productsContainer) return;

    let html = '';
    
    // Limit to 6 products for the overlay
    const displayProducts = products.slice(0, 6);
    
    displayProducts.forEach(product => {
      const image = product.featured_image || product.image || '';
      let imageUrl = '';
      if (image) {
        if (typeof image === 'string') {
          imageUrl = image;
        } else if (image.url) {
          imageUrl = image.url;
        } else if (image.src) {
          imageUrl = image.src;
        }
      }
      
      // Format price
      let price = '';
      let comparePrice = '';
      if (product.price) {
        price = typeof product.price === 'number' && product.price > 1000 
          ? (product.price / 100).toFixed(2) 
          : parseFloat(product.price).toFixed(2);
      }
      if (product.compare_at_price) {
        comparePrice = typeof product.compare_at_price === 'number' && product.compare_at_price > 1000
          ? (product.compare_at_price / 100).toFixed(2)
          : parseFloat(product.compare_at_price).toFixed(2);
      }
      
      const isOnSale = comparePrice && parseFloat(comparePrice) > parseFloat(price);
      const productTitle = product.title || 'Untitled Product';
      const productVendor = product.vendor || '';
      const productUrl = product.url || `${window.Shopify.routes.root}products/${product.handle || ''}`;
      
      // Use the same product card structure as the theme
      html += `
        <div class="product-card" data-product-id="${product.id || ''}">
          <div class="product-card__image-wrapper">
            <a href="${productUrl}" class="product-card__link" aria-label="${productTitle}">
              ${imageUrl ? `
                <img
                  src="${imageUrl}"
                  alt="${productTitle}"
                  class="product-card__image"
                  loading="lazy"
                  width="400"
                  height="400"
                >
              ` : `
                <div class="product-card__image product-card__image--placeholder">
                  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                    <rect fill="#f3f4f6" width="100" height="100"/>
                    <text x="50" y="55" text-anchor="middle" font-size="12" fill="#9ca3af">No image</text>
                  </svg>
                </div>
              `}
            </a>
            ${isOnSale ? '<div class="product-card__badges"><span class="product-card__badge product-card__badge--sale">Sale</span></div>' : ''}
          </div>
          <div class="product-card__info">
            ${productVendor ? `<p class="product-card__vendor">${productVendor}</p>` : ''}
            <h3 class="product-card__title">
              <a href="${productUrl}">${productTitle}</a>
            </h3>
            <div class="product-card__price">
              ${isOnSale ? `
                <span class="product-card__price--sale">$${price}</span>
                <span class="product-card__price--compare">$${comparePrice}</span>
              ` : `
                <span class="product-card__price--regular">$${price}</span>
              `}
            </div>
          </div>
        </div>
      `;
    });
    
    productsContainer.innerHTML = html;
  };

  // Handle input changes
  const handleSearchInput = (e) => {
    const query = e.target.value.trim();
    
    // Update clear button visibility
    if (clearButton) {
      if (query.length > 0) {
        clearButton.classList.add('is-visible');
      } else {
        clearButton.classList.remove('is-visible');
      }
    }
    
    // Debounce search
    clearTimeout(searchTimeout);
    
    if (query.length >= 2) {
      searchTimeout = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      showSuggestions();
    }
  };

  // Initialize search
  const initSearch = () => {
    // Get all elements
    searchOverlay = document.querySelector('[data-search-overlay]');
    searchInput = document.querySelector('.search-overlay__input');
    searchForm = document.querySelector('[data-search-form]');
    clearButton = document.querySelector('[data-search-clear]');
    suggestionsSection = document.querySelector('[data-search-suggestions]');
    resultsSection = document.querySelector('[data-search-results]');
    productsContainer = document.querySelector('[data-search-products]');
    loadingSection = document.querySelector('[data-search-loading]');
    noResultsSection = document.querySelector('[data-search-no-results]');
    resultsTitle = document.querySelector('[data-search-results-title]');
    seeAllLink = document.querySelector('[data-search-see-all]');
    seeAllEmptyLink = document.querySelector('[data-search-see-all-empty]');
    searchQuerySpan = document.querySelector('[data-search-query]');

    if (!searchOverlay) {
      console.warn('[Search] Search overlay not found');
      return;
    }

    // Search icon toggles open/close
    document.querySelectorAll('[data-search-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isOpen) {
          closeSearch();
        } else {
          openSearch();
        }
      });
    });
    
    // Click on header area (outside form) closes search
    const headerElement = document.querySelector('[data-header]');
    if (headerElement) {
      headerElement.addEventListener('click', (e) => {
        // Only close if search is open and click is not on the search form
        if (isOpen && !e.target.closest('.search-overlay__form') && !e.target.closest('[data-search-open]')) {
          closeSearch();
        }
      });
    }

    // Close search buttons and backdrop
    document.querySelectorAll('[data-search-close]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        // Don't close if user was dragging carousel
        if (isDraggingCarousel) {
          isDraggingCarousel = false;
          return;
        }
        closeSearch();
      });
    });

    // Clear button
    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
          clearButton.classList.remove('is-visible');
          showSuggestions();
        }
      });
    }

    // Search input
    if (searchInput) {
      searchInput.addEventListener('input', handleSearchInput);
    }

    // Form submission
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const query = searchInput?.value.trim();
        if (!query) {
          e.preventDefault();
        }
        // Let form submit naturally to search page
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeSearch();
      }
    });

    console.log('[Search] Initialized successfully');
  };

  // Initialize drag scroll for search results carousel (1270px and below)
  // Uses smooth live scrolling with slow snap when scrolling stops
  const initSearchCarousel = () => {
    const productsContainer = document.querySelector('[data-search-products]');
    if (!productsContainer) return;
    
    let isMouseDown = false;
    let hasMoved = false;
    let startX = 0;
    let startScrollLeft = 0;
    let preventNextClick = false;
    let scrollEndTimeout = null;
    
    productsContainer.style.cursor = 'grab';
    // Disable CSS scroll-snap - we handle it in JS
    productsContainer.style.scrollSnapType = 'none';
    
    // Prevent native image drag
    const preventImageDrag = () => {
      productsContainer.querySelectorAll('img').forEach(img => {
        img.draggable = false;
      });
    };
    preventImageDrag();
    
    // Re-run when products are loaded
    const observer = new MutationObserver(() => {
      preventImageDrag();
      productsContainer.style.scrollSnapType = 'none';
    });
    observer.observe(productsContainer, { childList: true, subtree: true });
    
    // Prevent clicks after drag
    productsContainer.addEventListener('click', (e) => {
      if (preventNextClick) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        preventNextClick = false;
      }
    }, true);
    
    // Get card width including gap
    const getCardWidth = () => {
      const firstCard = productsContainer.querySelector('.product-card');
      if (!firstCard) return 200;
      const style = window.getComputedStyle(productsContainer);
      const gap = parseFloat(style.gap) || 12;
      return firstCard.offsetWidth + gap;
    };
    
    // Snap to nearest card with SLOW smooth animation
    const snapToNearestCard = () => {
      const cardWidth = getCardWidth();
      if (cardWidth <= 0) return;
      
      const currentScroll = productsContainer.scrollLeft;
      const maxScroll = productsContainer.scrollWidth - productsContainer.clientWidth;
      
      // Snap to nearest single card
      const cardIndex = Math.round(currentScroll / cardWidth);
      let targetScroll = cardIndex * cardWidth;
      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
      
      // SLOW smooth scroll animation (800ms)
      const startScroll = productsContainer.scrollLeft;
      const distance = targetScroll - startScroll;
      
      // Don't animate if already close
      if (Math.abs(distance) < 5) return;
      
      const duration = 800; // Slower animation
      const startTime = performance.now();
      
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        productsContainer.scrollLeft = startScroll + (distance * easedProgress);
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    };
    
    // Mouse events - LIVE scrolling during drag
    productsContainer.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('button, [data-quick-add]')) return;
      if (window.innerWidth > 1270) return;
      
      // Clear any pending snap
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
        scrollEndTimeout = null;
      }
      
      isMouseDown = true;
      hasMoved = false;
      preventNextClick = false;
      isDraggingCarousel = true; // Set flag to prevent overlay close
      startX = e.pageX;
      startScrollLeft = productsContainer.scrollLeft;
      productsContainer.style.cursor = 'grabbing';
      e.preventDefault();
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;
      const deltaX = e.pageX - startX;
      if (Math.abs(deltaX) > 0) hasMoved = true;
      // LIVE update scroll position during drag
      productsContainer.scrollLeft = startScrollLeft - deltaX;
    });
    
    document.addEventListener('mouseup', (e) => {
      if (!isMouseDown) return;
      isMouseDown = false;
      productsContainer.style.cursor = 'grab';
      
      if (hasMoved) {
        preventNextClick = true;
        hasMoved = false;
        // Snap after a short delay when drag ends
        scrollEndTimeout = setTimeout(() => {
          snapToNearestCard();
          // Reset drag flag after snap completes
          setTimeout(() => { isDraggingCarousel = false; }, 100);
        }, 50);
        setTimeout(() => { preventNextClick = false; }, 150);
        
        // Prevent closing overlay if mouse released outside
        e.stopPropagation();
      } else {
        // Reset flag if no movement
        isDraggingCarousel = false;
      }
    });
    
    // Touch support - LIVE scrolling during swipe
    let touchStartX = 0;
    let touchStartScrollLeft = 0;
    let touchHasMoved = false;
    
    productsContainer.addEventListener('touchstart', (e) => {
      if (window.innerWidth > 1270) return;
      
      // Clear any pending snap
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
        scrollEndTimeout = null;
      }
      
      touchStartX = e.touches[0].pageX;
      touchStartScrollLeft = productsContainer.scrollLeft;
      touchHasMoved = false;
    }, { passive: true });
    
    productsContainer.addEventListener('touchmove', (e) => {
      if (window.innerWidth > 1270) return;
      const deltaX = e.touches[0].pageX - touchStartX;
      if (Math.abs(deltaX) > 0) touchHasMoved = true;
      // LIVE update scroll position during swipe
      productsContainer.scrollLeft = touchStartScrollLeft - deltaX;
    }, { passive: true });
    
    productsContainer.addEventListener('touchend', () => {
      if (touchHasMoved) {
        // Snap after swipe ends
        scrollEndTimeout = setTimeout(() => {
          snapToNearestCard();
        }, 50);
      }
    });
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSearch();
      initSearchCarousel();
    });
  } else {
    initSearch();
    initSearchCarousel();
  }
})();
